import { useEffect } from 'react';
import { DateValue, Dialog, DialogTrigger, Label, Modal } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { updateReservationPeriod } from '@/api/updateReservationPeriod';
import Button from '@/components/Button/Button';
import TimeField from '@/components/TimeField/TimeField';
import { NotificationAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import { DetailedAttendanceReservation } from '@/types/attendanceReservation';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedDate: DateValue;
  reservation: DetailedAttendanceReservation;
  afterSubmit?: () => void;
};

type FormData = {
  start_time: string;
  end_time: string;
};

const UpsertReservationPeriodDialog = ({
  isOpen,
  setIsOpen,
  selectedDate,
  reservation,
  afterSubmit,
}: Props) => {
  const schema = yup.object().shape({
    start_time: yup.string().required('開始時間を入力してください'),
    end_time: yup.string().required('終了時間を入力してください'),
  });

  const [_, setNotification] = useAtom(NotificationAtom);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      start_time: reservation.start_time,
      end_time: reservation.end_time,
    },
  });

  const onSubmit = (data: FormData) => {
    const updatePeriod = async () => {
      try {
        const result = await updateReservationPeriod(
          reservation.id,
          data.start_time,
          data.end_time
        );
        if (result.status === 200) {
          setIsOpen(false);
          afterSubmit && afterSubmit();
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '来所予約時間を更新しました',
            message: '来所予約時間を更新しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '来所予約時間の更新に失敗しました',
            message: '来所予約時間の更新に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '来所予約時間の更新に失敗しました',
          message: '来所予約時間の更新に失敗しました',
        });
      }
    };
    updatePeriod();
  };

  useEffect(() => {
    if (!reservation) return;
    const defaultPeriod = {
      start_time: '00:00:00',
      end_time: '00:00:00',
    };
    if (reservation.start_time === '00:00:00' && reservation.end_time === '00:00:00') {
      if (reservation.pickup_info?.isRequired) {
        defaultPeriod.start_time = reservation.pickup_info.pickup_time ?? '00:00:00';
      } else {
        defaultPeriod.start_time = reservation.pickup_info?.arrival_time ?? '00:00:00';
      }

      if (reservation.dropoff_info?.isRequired) {
        defaultPeriod.end_time = reservation.dropoff_info.dropoff_time ?? '00:00:00';
      } else {
        defaultPeriod.end_time = reservation.dropoff_info?.departure_time ?? '00:00:00';
      }
      reset(defaultPeriod);
    }
  }, [reservation]);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/50">
        <Dialog className="h-max w-max">
          <form
            className="space-y-4"
            onSubmit={handleSubmit(onSubmit, (errors) => {
              console.log('Validation errors:', errors);
            })}
          >
            <div className="space-y-4 rounded-lg bg-white p-6">
              <h2 className="text-lg font-bold">終始時間の変更</h2>
              <div className="flex flex-row justify-between space-x-4">
                <div className="flex w-full flex-col">
                  <Label className="mb-2 text-base md:text-lg md:font-bold">日付</Label>
                  <span>
                    {formattedDate(selectedDate.toDate('Asia/Tokyo'), 'yyyy年MM月dd日(E)')}
                  </span>
                </div>
                <div className="flex w-full flex-col">
                  <Label className="mb-2 text-base md:text-lg md:font-bold">氏名</Label>
                  <span>{reservation.child.last_name + ' ' + reservation.child.first_name}</span>
                </div>
              </div>
              <div className="mt-4 flex items-center space-x-4">
                <div className="flex flex-col">
                  <TimeField
                    name="start_time"
                    label="開始時間"
                    control={control}
                    required
                    errors={errors.start_time}
                  />
                </div>
                <div className="flex flex-col">
                  <TimeField
                    name="end_time"
                    label="終了時間"
                    control={control}
                    required
                    errors={errors.end_time}
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-center space-x-4">
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                >
                  キャンセル
                </Button>
                <Button
                  size="lg"
                  variant="solid-fill"
                  type="submit"
                  disabled={!isValid}
                >
                  保存
                </Button>
              </div>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};

export default UpsertReservationPeriodDialog;
